import React, { useMemo, useState } from "react";
import { Check, XCircle, X, Table, Clock, Calendar, Mail } from "lucide-react";

/**
 * ReservationDecisionModal — the owner's Confirm / Decline moment.
 *
 * Two modes:
 *   • kind="confirm"  — asks for an (optional) table number to pre-assign
 *   • kind="decline"  — auto-suggests 3 alternative time slots so the
 *                       customer isn't just told "no" — they get options
 *
 * The parent supplies `onSubmit(payload)` which posts to
 * PATCH /api/admin/reservations/{id}/status with the extra fields.
 */
const TABLE_SUGGESTIONS = ["1", "2", "3", "4", "5", "6", "7", "8", "9", "10", "11", "12"];

function pad(n) { return String(n).padStart(2, "0"); }

function timeToParts(t) {
  // Accepts "7:00 PM" | "19:00" | anything similar. Returns {h,m} in 24h.
  if (!t) return { h: 19, m: 0 };
  const m = t.trim().match(/^(\d{1,2}):(\d{2})\s*(AM|PM)?$/i);
  if (!m) return { h: 19, m: 0 };
  let h = parseInt(m[1], 10);
  const min = parseInt(m[2], 10);
  const suffix = (m[3] || "").toUpperCase();
  if (suffix === "PM" && h < 12) h += 12;
  if (suffix === "AM" && h === 12) h = 0;
  return { h, m: min };
}

function shiftTime({ h, m }, minutes) {
  const total = h * 60 + m + minutes;
  const nh = ((total % 1440) + 1440) % 1440 / 60;
  const nhInt = Math.floor(nh);
  const nm = ((total % 1440) + 1440) % 1440 - nhInt * 60;
  // Format as "7:30 PM"
  const ampm = nhInt >= 12 ? "PM" : "AM";
  const h12 = nhInt % 12 === 0 ? 12 : nhInt % 12;
  return `${h12}:${pad(nm)} ${ampm}`;
}

function shiftDate(dateStr, days) {
  if (!dateStr) return "";
  const [y, mo, d] = dateStr.split("-").map((x) => parseInt(x, 10));
  if (!y || !mo || !d) return "";
  const dt = new Date(Date.UTC(y, mo - 1, d));
  dt.setUTCDate(dt.getUTCDate() + days);
  return `${dt.getUTCFullYear()}-${pad(dt.getUTCMonth() + 1)}-${pad(dt.getUTCDate())}`;
}

function friendlyDate(dateStr) {
  if (!dateStr) return "";
  const [y, mo, d] = dateStr.split("-").map((x) => parseInt(x, 10));
  const dt = new Date(y, mo - 1, d);
  return dt.toLocaleDateString("en-AU", { weekday: "short", day: "numeric", month: "short" });
}

export default function ReservationDecisionModal({ open, kind, reservation, onSubmit, onClose }) {
  const [tableNumber, setTableNumber] = useState(reservation?.suggested_table || "");
  const [selectedAlts, setSelectedAlts] = useState([]);
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);

  const alternatives = useMemo(() => {
    if (!reservation) return [];
    const parts = timeToParts(reservation.time);
    return [
      { label: "Same day, 1 hour earlier",
        date: reservation.date, time: shiftTime(parts, -60) },
      { label: "Same day, 1 hour later",
        date: reservation.date, time: shiftTime(parts, +60) },
      { label: "Next day, same time",
        date: shiftDate(reservation.date, 1), time: reservation.time },
    ];
  }, [reservation]);

  React.useEffect(() => {
    if (open) {
      setTableNumber(reservation?.suggested_table || "");
      // Pre-select all 3 alternatives for the decline flow
      setSelectedAlts(kind === "decline" ? alternatives.map((_, i) => i) : []);
      setNote("");
    }
  }, [open, kind, reservation, alternatives]);

  if (!open || !reservation) return null;
  const isConfirm = kind === "confirm";

  const submit = async () => {
    setBusy(true);
    try {
      const payload = isConfirm
        ? { status: "confirmed", table_number: tableNumber.trim() || null, note: note.trim() || null }
        : { status: "declined",
            alternative_times: selectedAlts.map((i) => alternatives[i]),
            note: note.trim() || null };
      await onSubmit(payload);
      onClose();
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-[200] bg-black/70 backdrop-blur-sm flex items-center justify-center p-4"
      data-testid="reservation-decision-modal"
      onClick={onClose}
    >
      <div
        className="bg-[var(--mm-bg-alt)] border border-[var(--mm-accent)]/40 max-w-lg w-full p-6 shadow-2xl relative"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          onClick={onClose}
          className="absolute top-3 right-3 text-[var(--mm-cream-muted)] hover:text-[var(--mm-cream)]"
          aria-label="Close"
          data-testid="reservation-modal-close"
        >
          <X size={18} />
        </button>

        <div className={`text-[10px] tracking-[0.3em] uppercase mb-2 ${isConfirm ? "text-emerald-400" : "text-red-400"}`}>
          {isConfirm ? "Confirm Reservation" : "Decline & Offer Alternatives"}
        </div>
        <h3 className="font-display italic text-3xl text-[var(--mm-cream)] mb-2 leading-tight">
          {reservation.name || "Guest"}
        </h3>
        <div className="text-sm text-[var(--mm-cream-muted)] mb-5 flex flex-wrap gap-4">
          <span className="inline-flex items-center gap-1"><Calendar size={13} /> {friendlyDate(reservation.date)}</span>
          <span className="inline-flex items-center gap-1"><Clock size={13} /> {reservation.time}</span>
          <span className="inline-flex items-center gap-1">Party of {reservation.party_size || "?"}</span>
        </div>

        {isConfirm ? (
          <>
            <label className="block text-[10px] tracking-[0.24em] uppercase text-[var(--mm-cream-muted)] mb-2 flex items-center gap-1.5">
              <Table size={12} /> Pre-assign table (optional)
            </label>
            <div className="flex flex-wrap gap-1.5 mb-3">
              {TABLE_SUGGESTIONS.map((n) => (
                <button
                  key={n}
                  onClick={() => setTableNumber(n)}
                  data-testid={`table-suggest-${n}`}
                  className={`text-[11px] px-2.5 py-1 border transition-all ${
                    tableNumber === n
                      ? "bg-[var(--mm-accent)] text-[var(--mm-bg)] border-[var(--mm-accent)]"
                      : "border-[var(--mm-line)] text-[var(--mm-cream-muted)] hover:border-[var(--mm-accent)]"
                  }`}
                >
                  {n}
                </button>
              ))}
            </div>
            <input
              type="text"
              value={tableNumber}
              onChange={(e) => setTableNumber(e.target.value)}
              placeholder="Or type any table label (e.g. Booth 3)"
              data-testid="confirm-table-input"
              className="w-full bg-transparent border border-[var(--mm-line)] text-[var(--mm-cream)] placeholder:text-[var(--mm-cream-muted)]/60 px-3 py-2 text-sm focus:border-[var(--mm-accent)] outline-none mb-4"
            />
          </>
        ) : (
          <>
            <div className="text-[10px] tracking-[0.24em] uppercase text-[var(--mm-cream-muted)] mb-2">
              Offer alternative times (email &amp; SMS to customer)
            </div>
            <div className="space-y-2 mb-4">
              {alternatives.map((alt, i) => {
                const on = selectedAlts.includes(i);
                return (
                  <button
                    key={i}
                    onClick={() =>
                      setSelectedAlts((prev) =>
                        prev.includes(i) ? prev.filter((x) => x !== i) : [...prev, i],
                      )
                    }
                    data-testid={`alt-time-${i}`}
                    className={`w-full text-left px-3 py-2.5 border transition-all flex items-center justify-between ${
                      on
                        ? "border-[var(--mm-accent)] bg-[var(--mm-accent)]/10 text-[var(--mm-cream)]"
                        : "border-[var(--mm-line)] text-[var(--mm-cream-muted)] hover:border-[var(--mm-accent)]/60"
                    }`}
                  >
                    <div>
                      <div className="text-sm">{alt.label}</div>
                      <div className="text-[11px] text-[var(--mm-cream-muted)]">
                        {friendlyDate(alt.date)} · {alt.time}
                      </div>
                    </div>
                    {on && <Check size={16} className="text-[var(--mm-accent)]" />}
                  </button>
                );
              })}
            </div>
          </>
        )}

        <label className="block text-[10px] tracking-[0.24em] uppercase text-[var(--mm-cream-muted)] mb-1.5 flex items-center gap-1.5">
          <Mail size={12} /> Personal note (optional, added to the email)
        </label>
        <textarea
          value={note}
          onChange={(e) => setNote(e.target.value)}
          placeholder={isConfirm ? "e.g. Enjoy your anniversary — we've got you a candle." : "e.g. Sorry — we're fully booked for a private event."}
          rows={2}
          data-testid="reservation-note"
          className="w-full bg-transparent border border-[var(--mm-line)] text-[var(--mm-cream)] placeholder:text-[var(--mm-cream-muted)]/60 px-3 py-2 text-sm focus:border-[var(--mm-accent)] outline-none resize-none mb-5"
        />

        <button
          onClick={submit}
          disabled={busy || (!isConfirm && selectedAlts.length === 0)}
          data-testid="reservation-submit"
          className={`w-full inline-flex items-center justify-center gap-2 py-3 text-xs tracking-[0.24em] uppercase font-semibold transition-all disabled:opacity-50 disabled:cursor-not-allowed ${
            isConfirm
              ? "bg-emerald-600 text-white hover:bg-emerald-500"
              : "bg-red-600 text-white hover:bg-red-500"
          }`}
        >
          {isConfirm ? <Check size={14} /> : <XCircle size={14} />}
          {busy ? "Sending..." : isConfirm
            ? (tableNumber ? `Confirm & assign table ${tableNumber}` : "Confirm reservation")
            : `Decline & offer ${selectedAlts.length} alternative${selectedAlts.length === 1 ? "" : "s"}`}
        </button>
        {!isConfirm && selectedAlts.length === 0 && (
          <div className="text-[10px] text-[var(--mm-cream-muted)] text-center mt-2">
            Pick at least one alternative time — or close and just Decline without options.
          </div>
        )}
      </div>
    </div>
  );
}
